package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.AuthenticationError
import fr.manyverse.app.domain.error.DomainException
import fr.manyverse.app.domain.model.OAuthProvider
import fr.manyverse.app.domain.model.User
import fr.manyverse.app.domain.repository.AuthRepository
import fr.manyverse.app.getPlatform
import fr.manyverse.app.infrastructure.auth.OAuthCallbackFailures
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.annotations.SupabaseExperimental
import io.github.jan.supabase.auth.OtpType
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.event.AuthEvent
import io.github.jan.supabase.auth.providers.Google
import io.github.jan.supabase.auth.providers.builtin.OTP
import io.github.jan.supabase.auth.status.SessionStatus
import io.github.jan.supabase.exceptions.HttpRequestException
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.merge

private const val TAG = "AuthRepository"

class SupabaseAuthRepository(
    private val supabaseClient: SupabaseClient,
    private val oauthCallbackFailures: OAuthCallbackFailures
) : AuthRepository {

    override suspend fun signOut(): Result<Unit> {
        return try {
            supabaseClient.auth.signOut()
            Result.success(Unit)
        } catch (_: HttpRequestException) {
            // Hors ligne, supabase-kt lève avant d'effacer la session : elle serait rechargée au
            // prochain lancement alors que l'utilisateur s'est déconnecté. Le refresh token reste
            // valide côté serveur, mais n'est plus stocké nulle part.
            AppLogger.warn(TAG, "Déconnexion hors ligne : session effacée localement uniquement")
            supabaseClient.auth.clearSession()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    /**
     * Returns the currently authenticated user, waiting for Supabase to finish
     * loading the session from disk before deciding. Without this wait, calls at
     * app startup race the SessionManager and can incorrectly report null.
     */
    override suspend fun getCurrentUser(): Result<User?> {
        return try {
            val resolved = supabaseClient.auth.sessionStatus
                .first { it !is SessionStatus.Initializing }

            val user = when (resolved) {
                is SessionStatus.Authenticated -> resolved.session.user?.let {
                    User(id = it.id, email = it.email ?: "")
                }
                else -> null
            }
            Result.success(user)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    /**
     * Deletes the current user's account via a Postgres RPC function.
     * Requires a `delete_account()` function created in Supabase with SECURITY DEFINER.
     */
    override suspend fun deleteAccount(): Result<Unit> {
        return try {
            supabaseClient.postgrest.rpc("delete_account")
            runCatching { supabaseClient.auth.signOut() }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    override fun observeNewSession(): Flow<User> =
        supabaseClient.auth.sessionStatus
            .filterIsInstance<SessionStatus.Authenticated>()
            .mapNotNull { status ->
                if (!status.isNew) return@mapNotNull null
                status.session.user?.let { User(id = it.id, email = it.email ?: "") }
            }

    override fun observeSessionEnded(): Flow<Unit> = supabaseClient.auth.sessionStatus.sessionEnds()

    /**
     * Les erreurs de l'URL de retour (`error_code=...`) sont détectées par supabase-kt sur toutes les
     * plateformes, qui ne les signale que par un [AuthEvent.OtpError]. Ce sont des refus du serveur
     * (état OAuth expiré ou invalide, inscriptions fermées…) : ils partent dans Sentry.
     */
    @OptIn(SupabaseExperimental::class)
    override fun observeOAuthFailures(): Flow<DomainException> = merge(
        oauthCallbackFailures.failures,
        supabaseClient.auth.events
            .filterIsInstance<AuthEvent.OtpError>()
            .map { event ->
                AuthenticationError("OAuth callback rejected: ${event.error}").also {
                    AppLogger.error(TAG, "Retour OAuth en erreur (${event.error})", it)
                }
            }
    )

    override suspend fun sendOtpToEmail(email: String): Result<Unit> {
        return try {
            supabaseClient.auth.signInWith(OTP) {
                this.email = email
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    override suspend fun verifyEmailOtp(email: String, token: String): Result<User> {
        return try {
            supabaseClient.auth.verifyEmailOtp(
                type = OtpType.Email.EMAIL,
                email = email,
                token = token
            )
            val currentUser = supabaseClient.auth.currentUserOrNull()
            if (currentUser != null) {
                Result.success(User(id = currentUser.id, email = currentUser.email ?: email))
            } else {
                Result.failure(mapToDomainException(Exception("OTP verified but no session found"), "auth"))
            }
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    override suspend fun signInAnonymously(): Result<User> {
        return try {
            supabaseClient.auth.signInAnonymously()
            val currentUser = supabaseClient.auth.currentUserOrNull()
            if (currentUser != null) {
                Result.success(User(id = currentUser.id, email = ""))
            } else {
                Result.failure(mapToDomainException(Exception("Anonymous sign-in failed"), "auth"))
            }
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }

    override suspend fun signInWithOAuth(provider: OAuthProvider): Result<Unit> {
        return try {
            val redirectUrl = getPlatform().oauthRedirectUrl
            when (provider) {
                OAuthProvider.GOOGLE -> supabaseClient.auth.signInWith(Google, redirectUrl = redirectUrl) {
                    scopes.addAll(listOf("openid", "email"))
                }
                OAuthProvider.APPLE -> supabaseClient.auth.signInWith(
                    provider = object : io.github.jan.supabase.auth.providers.OAuthProvider() {
                        override val name = "apple"
                    },
                    redirectUrl = redirectUrl
                ) {
                    scopes.addAll(listOf("email"))
                }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "auth"))
        }
    }
}

/**
 * Émet quand la session disparaît alors qu'une session existait : supabase-kt l'efface lui-même
 * quand le refresh token est refusé (révoqué, compte supprimé), et à chaque déconnexion.
 *
 * Un échec de refresh dû au réseau ou à une erreur 5xx n'émet rien : supabase-kt passe en
 * `RefreshFailure` et retente toutes les 10 s jusqu'à rétablir la session. Déconnecter à ce
 * moment-là ferait perdre sa session à un utilisateur simplement hors ligne.
 */
internal fun Flow<SessionStatus>.sessionEnds(): Flow<Unit> = flow {
    var hadSession = false
    var refreshFailing = false
    collect { status ->
        when (status) {
            is SessionStatus.RefreshFailure -> {
                // supabase-kt ne logge la cause réseau qu'en debug : ce breadcrumb explique les
                // requêtes en 401 qui peuvent suivre. Une seule fois par série, les tentatives se
                // répétant toutes les 10 s
                if (!refreshFailing) {
                    AppLogger.warn(TAG, "Refresh de session en échec (réseau ou 5xx), nouvelles tentatives")
                }
                hadSession = true
                refreshFailing = true
            }
            is SessionStatus.Authenticated -> {
                hadSession = true
                refreshFailing = false
            }
            is SessionStatus.NotAuthenticated -> {
                if (hadSession) emit(Unit)
                hadSession = false
                refreshFailing = false
            }
            is SessionStatus.Initializing -> Unit
        }
    }
}
